import type {
  ApiResponse,
  Album,
  ArchiveMonth,
  ArchiveListQuery,
  AuthType,
  CaptchaData,
  Category,
  Comment,
  CommentSubmitSetting,
  CommentAnchorResult,
  CommentCreateInput,
  Content,
  ContentListQuery,
  CustomFieldDef,
  FriendLink,
  FriendLinkListQuery,
  FriendLinkListResult,
  FriendLinkRSSListQuery,
  FriendLinkRSSItem,
  FriendLinkStatus,
  GlobalSetting,
  HotContentListQuery,
  LatestComment,
  LinkApplyInput,
  LikeActionResult,
  PageResult,
  RandomContent,
  RandomFriendLink,
  SiteStats,
  Tag
} from '~/types/blog'

function toQuery(params: Record<string, unknown>) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
  )
}

function resolveHttpStatusFromCode(code: number) {
  if (code >= 1000 && code < 1100) return 400
  if (code >= 1100 && code < 1200) return 401
  if (code >= 1200 && code < 1300) return 403
  if (code >= 1300 && code < 1400) return 404
  if (code >= 1400 && code < 1500) return 409
  if (code >= 1500 && code < 1600) return 429
  if (code >= 1600 && code < 1700) return 502
  if (code >= 1700 && code < 1800) return 500
  if (code >= 1800 && code < 1900) return 504
  return 500
}

type RequestOptions<T> = Parameters<typeof $fetch<ApiResponse<T>>>[1] & { throwError?: boolean }

function sanitizePageResult<T>(payload: PageResult<T> | null | undefined): PageResult<T> {
  const list = Array.isArray(payload?.list) ? payload.list.filter(item => item !== null && item !== undefined) as T[] : []
  return {
    ...(payload || {}),
    list,
    total: Number(payload?.total || 0)
  } as PageResult<T>
}

export const useBlogApi = () => {
  const config = useRuntimeConfig()
  const apiBase = config.public.apiBase

  async function request<T>(
    path: string,
    options: RequestOptions<T> = {}
  ) {
    const { throwError = true, ...fetchOptions } = options

    try {
      const response = await $fetch<ApiResponse<T>>(`${apiBase}${path}`, {
        ...fetchOptions,
        retry: 0
      })

      if (response.code !== 0 && throwError) {
        const statusCode = resolveHttpStatusFromCode(Number(response.code))
        throw createError({
          statusCode,
          statusMessage: response.msg || '接口请求失败',
          data: {
            businessCode: response.code,
            payload: response.data
          }
        })
      }
      return response.data
    } catch (error: any) {
      const backendCode = Number(error?.data?.code || error?.response?._data?.code || 0)
      if (throwError && backendCode > 0) {
        const statusCode = Number(error?.statusCode || resolveHttpStatusFromCode(backendCode))
        throw createError({
          statusCode,
          statusMessage: error?.data?.msg || error?.response?._data?.msg || '接口请求失败',
          data: {
            businessCode: backendCode,
            payload: error?.data?.data ?? error?.response?._data?.data
          }
        })
      }

      if (throwError) {
        throw error
      }
      // 静默模式返回 null 让业务逻辑自己决定
      return null as unknown as T
    }
  }

  return {
    getGlobalSetting: () => request<GlobalSetting>('/blog/setting/getGlobalSetting'),
    getFieldDefList: (scope?: string) => request<CustomFieldDef[]>('/blog/field/getFieldDefList', { query: toQuery({ scope }) }),
    getAuthTypeList: () => request<AuthType[]>('/blog/auth/getAuthTypeList'),

    getContentList: async (query: ContentListQuery = {}, options: RequestOptions<PageResult<Content>> = {}) => {
      const payload = await request<PageResult<Content>>('/blog/content/getContentList', {
        ...options,
        query: toQuery(query as Record<string, unknown>)
      })
      return sanitizePageResult(payload)
    },
    getContentMetaList: async (query: ContentListQuery = {}, options: RequestOptions<PageResult<Content>> = {}) => {
      const payload = await request<PageResult<Content>>('/blog/content/getContentMetaList', {
        ...options,
        query: toQuery(query as Record<string, unknown>)
      })
      return sanitizePageResult(payload)
    },

    getContent: (id: number, options: RequestOptions<Content> = {}) =>
      request<Content>('/blog/content/getContent', { ...options, query: { id } }),
    getContentBySlug: (slug: string, options: RequestOptions<Content> = {}) =>
      request<Content>('/blog/content/getContentBySlug', { ...options, query: { slug } }),
    getContentMetaBySlug: (slug: string, options: RequestOptions<Content> = {}) =>
      request<Content>('/blog/content/getContentMetaBySlug', { ...options, query: { slug } }),
    getRandomContent: () => request<RandomContent>('/blog/content/getRandomContent'),
    getHotContentList: (query: HotContentListQuery = {}, options: RequestOptions<Content[]> = {}) =>
      request<Content[]>('/blog/content/getHotContentList', {
        ...options,
        query: toQuery(query as Record<string, unknown>)
      }),
    likeContent: (id: number) => request<LikeActionResult>('/blog/content/likeContent', { method: 'POST', body: { id } }),

    getCategoryTree: (options: RequestOptions<Category[]> = {}) => request<Category[]>('/blog/category/getCategoryTree', options),
    getTagCloud: () => request<Tag[]>('/blog/category/getTagCloud'),
    getArchives: async (query: ArchiveListQuery = {}, options: RequestOptions<PageResult<ArchiveMonth>> = {}) => {
      const payload = await request<PageResult<ArchiveMonth>>('/blog/category/getArchives', {
        ...options,
        query: toQuery(query as Record<string, unknown>)
      })
      return sanitizePageResult(payload)
    },
    getSiteStats: () => request<SiteStats>('/blog/category/getSiteStats'),

    getAlbumList: (query: ContentListQuery = {}) =>
      request<PageResult<Album>>('/blog/album/getAlbumList', { query: toQuery(query as Record<string, unknown>) }),
    getAlbumBySlug: (slug: string, options: RequestOptions<Album> = {}) =>
      request<Album>('/blog/album/getAlbumBySlug', { ...options, query: { slug } }),
    likeAlbum: (id: number) => request<LikeActionResult>('/blog/album/likeAlbum', { method: 'POST', body: { id } }),

    getFriendLinkList: (query: FriendLinkListQuery = {}) =>
      request<FriendLinkListResult>('/blog/link/getFriendLinkList', { query: toQuery(query as Record<string, unknown>) }),
    getFriendLinkRSSList: async (query: FriendLinkRSSListQuery = {}, options: RequestOptions<PageResult<FriendLinkRSSItem>> = {}) => {
      const payload = await request<PageResult<FriendLinkRSSItem>>('/blog/link/getFriendLinkRSSList', {
        ...options,
        query: toQuery(query as Record<string, unknown>)
      })
      return sanitizePageResult(payload)
    },
    getFriendLinkStatus: (query: { email?: string; url?: string; page?: number; pageSize?: number }) =>
      request<PageResult<FriendLinkStatus>>('/blog/link/getFriendLinkStatus', { query: toQuery(query as Record<string, unknown>) }),
    getRandomFriendLink: () => request<RandomFriendLink>('/blog/link/getRandomFriendLink'),
    applyFriendLink: (payload: LinkApplyInput) => request<null>('/blog/link/applyFriendLink', { method: 'POST', body: payload }),

    getCommentTree: (contentId: number, query?: { page?: number; pageSize?: number }) =>
      request<PageResult<Comment>>('/blog/comment/getCommentTree', { query: toQuery({ contentId, ...(query || {}) }) }),
    getCommentAnchor: (contentId: number, commentId: number, pageSize?: number) =>
      request<CommentAnchorResult>('/blog/comment/getCommentAnchor', { query: toQuery({ contentId, commentId, pageSize }) }),
    getLatestComments: (limit = 8) => request<LatestComment[]>('/blog/comment/getLatestComments', { query: { limit } }),
    createComment: (payload: CommentCreateInput) => request<null>('/blog/comment/createComment', { method: 'POST', body: payload }),
    getCommentSubmitSetting: () => request<CommentSubmitSetting>('/blog/comment/getCommentSubmitSetting'),

    getCaptcha: () => request<CaptchaData>('/base/captcha', { method: 'POST' })
  }
}
